import { Stack, Card, ScrollArea, Text, Divider, Group } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import {
  TripResponse,
  VehicleStopResponse,
  VehicleTripResponse,
} from "types/trip.types";
import { VehicleTripCard } from "./VehicleTripCard";
import TripVehicleStopCard from "./TripVehicleStopCard";
import { ActionFunc } from "./helper";
import { ColorPalette } from "types/ColorSpectrum";
import EventRegistry from "utils/eventRegistry";
import { IconCalendar, IconHome } from "@tabler/icons-react";
import { formatDate, timePart } from "utils/string.helpers";
import dayjs from "dayjs";

interface TripDetailsPanelProps {
  vehicleTrip: VehicleTripResponse;
  timezone?: string;
  onAction?: ActionFunc;
  eventRegistry?: EventRegistry;
  adminMode?: boolean;
}

export default function TripDetailsPanel({
  vehicleTrip,
  timezone,
  onAction,
  eventRegistry,
  adminMode,
}: TripDetailsPanelProps) {
  const [focusedStopId, setFocusedStopId] = useState<number>();
  const colorSpectrum =
    ColorPalette[
      (vehicleTrip.additionalData.colorIndex ?? 0) % ColorPalette.length
    ];
  const stopRefs = useRef<Record<string, HTMLDivElement | null>>({});

  function scrollToStop(stop: VehicleStopResponse) {
    setFocusedStopId(stop.vehicleStopId);
    const key = String(stop.vehicleStopId ?? "");
    const node = stopRefs.current[key];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  useEffect(() => {
    if (eventRegistry) {
      eventRegistry?.registerEvent(
        "TRIPS.TRIPDETAILS",
        "focusOnVehicleStop",
        (params: { point: VehicleStopResponse }) => {
          scrollToStop(params.point);
        }
      );
    }
  }, [eventRegistry]);

  const timeline: {
    icon?: any;
    caption?: string;
    stop?: VehicleStopResponse;
    hideStopTimes?: boolean;
  }[] = [];
  let currentDate = formatDate(vehicleTrip.startedAt ?? "");
  timeline.push({
    icon: <IconCalendar size={16} color="gray" />,
    caption: currentDate + " " + timePart(vehicleTrip.startedAt ?? ""),
  });
  vehicleTrip?.vehicleStops?.forEach((stop, stopIndex) => {
    if (formatDate(stop.arrivedAt) != currentDate) {
      currentDate = formatDate(stop.arrivedAt);
      timeline.push({
        icon: <IconCalendar size={16} color="gray" />,
        caption: currentDate,
      });
    }
    if (stopIndex + 1 < (vehicleTrip?.vehicleStops?.length ?? 0))
      timeline.push({ stop });
  });

  timeline.push({
    icon: <IconHome size={16} color="gray" />,
    caption: formatDate(
      dayjs(vehicleTrip.startedAt)
        .add(vehicleTrip.duration, "minutes")
        .toDate(),
      false,
      true
    ),
  });

  return (
    <Stack gap="md" style={{ height: "100%" }} p={16}>
      <VehicleTripCard
        vehicleTrip={vehicleTrip}
        timezone={timezone}
        onAction={onAction}
        closeButton
        current
      />
      <Divider label="Stops" labelPosition="center" />
      <ScrollArea flex={1}>
        <Stack gap="sm">
          {timeline.map((item, idx) => {
            if (item.stop) {
              const stop = item.stop;
              return (
                <div
                  key={stop.vehicleStopId ?? idx}
                  ref={(el) => {
                    const key = String(stop.vehicleStopId ?? "");
                    stopRefs.current[key] = el;
                  }}
                >
                  <TripVehicleStopCard
                    key={stop.vehicleStopId ?? idx}
                    stop={stop}
                    timezone={timezone}
                    onAction={onAction}
                    colorSpectrum={colorSpectrum}
                    current={stop.vehicleStopId == focusedStopId}
                    adminMode={adminMode}
                    hideStopTimes={item.hideStopTimes}
                  />
                </div>
              );
            } else if (item.icon) {
              return (
                <Group gap="xs" align="center" justify="center">
                  {item.icon}
                  <Text c="dimmed" size="xs">
                    {item.caption}
                  </Text>
                </Group>
              );
            }
          })}
          {timeline.length === 0 && (
            <Text c="dimmed" ta="center">
              No vehicle stops available.
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
